/**
 * @file Image (ui) — vendored Gluestack image implementation.
 * @description Backs the `@/components/atoms/Image` re-export. Sizing comes
 * from the closed `size` enum. For the rare callers that need exact native
 * pixel dimensions (e.g. flags 20x14 / 24x16, injury 12x12), use
 * `size="none"` — it emits no h/w classes so the caller's `style`/`className`
 * owns sizing. On web the caller's explicit `style` is honored: the
 * `revert-layer` reset is merged with caller precedence (array form, caller
 * last) rather than overriding it.
 * @module components/ui/image
 */
import React from "react";
import { createImage } from "@gluestack-ui/core/image/creator";
import { Platform, Image as RNImage } from "react-native";
import { tva } from "@gluestack-ui/utils/nativewind-utils";
import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";

const imageStyle = tva({
  base: "max-w-full",
  variants: {
    size: {
      "2xs": "h-6 w-6",
      xs: "h-10 w-10",
      sm: "h-16 w-16",
      md: "h-20 w-20",
      lg: "h-24 w-24",
      xl: "h-32 w-32",
      "2xl": "h-64 w-64",
      full: "h-full w-full",
      none: "",
    },
  },
});

const UIImage = createImage({ Root: RNImage });

type ImageProps = VariantProps<typeof imageStyle> &
  React.ComponentProps<typeof UIImage>;
const Image = React.forwardRef<
  React.ComponentRef<typeof UIImage>,
  ImageProps & { className?: string }
>(function Image({ size = "md", className, style, ...props }, ref) {
  // On web, merge the revert-layer reset with the caller's style giving the
  // caller precedence (array form, caller last). This lets `size="none"`
  // callers pin exact pixel dimensions via `style` without the reset
  // clobbering them. Native passes the caller's style through untouched.
  const mergedStyle =
    Platform.OS === "web"
      ? [{ height: "revert-layer", width: "revert-layer" }, style]
      : style;
  return (
    <UIImage
      className={imageStyle({ size, class: className })}
      {...props}
      ref={ref}
      // @ts-expect-error : web-only revert-layer reset, merged caller-last
      style={mergedStyle}
    />
  );
});

Image.displayName = "Image";
export { Image };
